/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.PaymentRepository
import studio.oddpods.cauce.core.data.repository.cauce.RecurringScheduleRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.model.payment.CreatePaymentRequest
import studio.oddpods.cauce.core.model.payment.PaymentMethod
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.RecurringSchedule
import studio.oddpods.cauce.core.model.session.Session
import studio.oddpods.cauce.core.model.session.SessionStatus
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_detail_stop_schedule_error
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_load_failed
import studio.oddpods.cauce.feature.patients.generated.resources.patients_mark_paid_error
import studio.oddpods.cauce.feature.patients.generated.resources.patients_mark_paid_success_one
import kotlin.time.Clock

data class SessionDisplay(
    val session: Session,
    val dateDisplay: String,
    val feeDisplay: String,
    val amountPaidDisplay: String,
)

data class RecurringScheduleDisplay(
    val schedule: RecurringSchedule,
    val feeDisplay: String,
)

data class PatientDetailState(
    val patient: Patient? = null,
    val defaultFeeDisplay: String = "",
    val sessions: List<Session> = emptyList(),
    val sessionsDisplay: List<SessionDisplay> = emptyList(),
    val totalOwed: Long = 0,
    val totalOwedDisplay: String = "",
    val sessionsThisMonth: Int = 0,
    val lastSessionDate: String? = null,
    val isLoading: Boolean = true,
    val error: UiText? = null,
    val hasPendingReminder: Boolean = false,
    val activeSchedules: List<RecurringScheduleDisplay> = emptyList(),
    val stopScheduleDialog: String? = null,
    val markPaidDialog: MarkPaidDialogData? = null,
    val markingPaid: Boolean = false,
    val snackbar: UiText? = null,
)

sealed interface PatientDetailEvent {
    data class NavigateToLogSession(val patientId: String) : PatientDetailEvent
    data class NavigateToSendReminder(val patientId: String) : PatientDetailEvent
    data class NavigateToEditPatient(val patientId: String) : PatientDetailEvent
    data class NavigateToCreateRecurringSchedule(val patientId: String) : PatientDetailEvent
    data object PatientDeactivated : PatientDetailEvent
}

sealed interface PatientDetailAction {
    data object LogSessionClick : PatientDetailAction
    data object RegisterPaymentClick : PatientDetailAction
    data object SendReminderClick : PatientDetailAction
    data object EditClick : PatientDetailAction
    data object CreateRecurringScheduleClick : PatientDetailAction
    data object DeactivateClick : PatientDetailAction
    data object DeactivateAndWaiveClick : PatientDetailAction
    data class MarkPaidSessionToggled(val sessionId: String) : PatientDetailAction
    data class MarkPaidMethodChanged(val method: PaymentMethod) : PatientDetailAction
    data class StopScheduleClick(val scheduleId: String) : PatientDetailAction
    data object ConfirmStopSchedule : PatientDetailAction
    data object DismissStopScheduleDialog : PatientDetailAction
    data object ConfirmMarkPaid : PatientDetailAction
    data object DismissMarkPaidDialog : PatientDetailAction
    data object SnackbarDismissed : PatientDetailAction
}

class PatientDetailViewModel(
    private val patientId: String,
    private val patientRepository: PatientRepository,
    private val sessionRepository: SessionRepository,
    private val userDataRepository: UserDataRepository,
    private val currencyFormatter: CurrencyFormatter,
    private val dateFormatter: DateFormatter,
    private val reminderRepository: ReminderRepository,
    private val paymentRepository: PaymentRepository,
    private val recurringScheduleRepository: RecurringScheduleRepository,
) : BaseViewModel<PatientDetailState, PatientDetailEvent, PatientDetailAction>(PatientDetailState()) {

    init {
        loadData()
        observePendingReminders()
        observeSchedules()
    }

    @Suppress("CyclomaticComplexMethod")
    override fun handleAction(action: PatientDetailAction) {
        when (action) {
            PatientDetailAction.LogSessionClick -> {
                sendEvent(PatientDetailEvent.NavigateToLogSession(patientId))
            }

            PatientDetailAction.RegisterPaymentClick -> openMarkPaidDialog()
            PatientDetailAction.SendReminderClick -> {
                sendEvent(PatientDetailEvent.NavigateToSendReminder(patientId))
            }

            PatientDetailAction.EditClick -> {
                sendEvent(PatientDetailEvent.NavigateToEditPatient(patientId))
            }

            PatientDetailAction.CreateRecurringScheduleClick -> {
                sendEvent(PatientDetailEvent.NavigateToCreateRecurringSchedule(patientId))
            }

            is PatientDetailAction.StopScheduleClick -> {
                mutableStateFlow.value = state.copy(stopScheduleDialog = action.scheduleId)
            }

            PatientDetailAction.ConfirmStopSchedule -> confirmStopSchedule()
            PatientDetailAction.DismissStopScheduleDialog -> {
                mutableStateFlow.value = state.copy(stopScheduleDialog = null)
            }

            PatientDetailAction.DeactivateClick -> deactivatePatient()
            PatientDetailAction.DeactivateAndWaiveClick -> deactivatePatientWithWaiver()
            is PatientDetailAction.MarkPaidSessionToggled -> handleMarkPaidToggle(action.sessionId)
            is PatientDetailAction.MarkPaidMethodChanged -> handleMarkPaidMethod(action.method)
            PatientDetailAction.ConfirmMarkPaid -> confirmMarkPaid()
            PatientDetailAction.DismissMarkPaidDialog -> {
                mutableStateFlow.value = state.copy(markPaidDialog = null)
            }

            PatientDetailAction.SnackbarDismissed -> {
                mutableStateFlow.value = state.copy(snackbar = null)
            }
        }
    }

    private fun handleMarkPaidToggle(sessionId: String) {
        val dialog = state.markPaidDialog ?: return
        mutableStateFlow.value = state.copy(
            markPaidDialog = dialog.copy(
                sessions = dialog.sessions.map { s ->
                    if (s.sessionId == sessionId) s.copy(selected = !s.selected) else s
                },
            ),
        )
    }

    private fun handleMarkPaidMethod(method: PaymentMethod) {
        val dialog = state.markPaidDialog ?: return
        mutableStateFlow.value = state.copy(
            markPaidDialog = dialog.copy(method = method),
        )
    }

    private fun deactivatePatient() {
        viewModelScope.launch {
            patientRepository.deactivatePatient(patientId)
                .onSuccess {
                    sendEvent(PatientDetailEvent.PatientDeactivated)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        error = e.toUiText(Res.string.patients_error_load_failed),
                    )
                }
        }
    }

    private fun deactivatePatientWithWaiver() {
        viewModelScope.launch {
            sessionRepository.waiveUnpaidByPatient(patientId)
                .onSuccess {
                    patientRepository.deactivatePatient(patientId)
                        .onSuccess {
                            sendEvent(PatientDetailEvent.PatientDeactivated)
                        }
                        .onFailure { e ->
                            mutableStateFlow.value = state.copy(
                                error = e.toUiText(Res.string.patients_error_load_failed),
                            )
                        }
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        error = e.toUiText(Res.string.patients_error_load_failed),
                    )
                }
        }
    }

    private fun openMarkPaidDialog() {
        if (state.markingPaid) return
        val pendingSessions = state.sessions.filter {
            it.sessionStatus == SessionStatus.CONFIRMED &&
                it.paymentStatus != PaymentStatus.PAID &&
                it.paymentStatus != PaymentStatus.WAIVED
        }
        if (pendingSessions.isEmpty()) return
        mutableStateFlow.value = state.copy(
            markPaidDialog = MarkPaidDialogData(
                sessions = pendingSessions.map {
                    MarkPaidSessionSelection(
                        sessionId = it.id,
                        sessionDate = dateFormatter.format(it.sessionDate) ?: it.sessionDate,
                        amountOwed = it.fee - it.amountPaid,
                    )
                },
            ),
        )
    }

    private fun confirmMarkPaid() {
        val dialog = state.markPaidDialog ?: return
        val selected = dialog.sessions.filter { it.selected }
        if (selected.isEmpty()) {
            mutableStateFlow.value = state.copy(markPaidDialog = null)
            return
        }
        mutableStateFlow.value = state.copy(markingPaid = true)
        viewModelScope.launch {
            val todayIso = Clock.System.now()
                .toLocalDateTime(TimeZone.currentSystemDefault())
                .date.toString()
            var failed = false
            for (s in selected) {
                val result = paymentRepository.createPayment(
                    CreatePaymentRequest(
                        sessionId = s.sessionId,
                        patientId = patientId,
                        amount = s.amountOwed,
                        method = dialog.method.name,
                        notes = null,
                        receivedAt = todayIso,
                    ),
                )
                if (result.isFailure) {
                    failed = true
                    break
                }
            }
            if (failed) {
                mutableStateFlow.value = state.copy(
                    markingPaid = false,
                    snackbar = UiText.Resource(Res.string.patients_mark_paid_error),
                )
                return@launch
            }
            val successMsg = if (selected.size == 1) {
                UiText.Resource(Res.string.patients_mark_paid_success_one)
            } else {
                UiText.Dynamic("${selected.size} pagos registrados")
            }
            mutableStateFlow.value = state.copy(
                markingPaid = false,
                markPaidDialog = null,
                snackbar = successMsg,
            )
        }
    }

    private fun observeSchedules() {
        viewModelScope.launch {
            recurringScheduleRepository.getSchedulesForPatient(patientId)
                .map { list -> list.filter { it.isActive } }
                .catch { /* schedules are supplementary — don't block the screen */ }
                .collect { activeSchedules ->
                    mutableStateFlow.value = state.copy(
                        activeSchedules = activeSchedules.map { schedule ->
                            RecurringScheduleDisplay(
                                schedule = schedule,
                                feeDisplay = currencyFormatter.format(schedule.fee),
                            )
                        },
                    )
                }
        }
    }

    private fun confirmStopSchedule() {
        val scheduleId = state.stopScheduleDialog ?: return
        mutableStateFlow.value = state.copy(stopScheduleDialog = null)
        viewModelScope.launch {
            recurringScheduleRepository.deactivateSchedule(scheduleId)
                .onFailure {
                    mutableStateFlow.value = state.copy(
                        snackbar = UiText.Resource(Res.string.patients_detail_stop_schedule_error),
                    )
                }
        }
    }

    private fun observePendingReminders() {
        viewModelScope.launch {
            reminderRepository.getPendingReminders()
                .map { list -> list.any { it.patientId == patientId } }
                .collect { hasPending ->
                    mutableStateFlow.value = state.copy(hasPendingReminder = hasPending)
                }
        }
    }

    private fun loadData() {
        mutableStateFlow.value = state.copy(isLoading = true, error = null)
        viewModelScope.launch {
            combine(
                patientRepository.observePatient(patientId).filterNotNull(),
                sessionRepository.getSessionsByPatient(patientId),
                userDataRepository.observeDisplayPreferences,
            ) { patient, sessions, _ -> Pair(patient, sessions) }
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = e.toUiText(Res.string.patients_error_load_failed),
                    )
                }
                .collect { (patient, sessions) ->
                    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
                    val currentMonth = "${now.year}-${now.month.number.toString().padStart(2, '0')}"

                    val confirmed = sessions.filter { it.sessionStatus == SessionStatus.CONFIRMED }

                    val totalOwed = confirmed
                        .filter { it.paymentStatus != PaymentStatus.PAID && it.paymentStatus != PaymentStatus.WAIVED }
                        .sumOf { it.fee - it.amountPaid }

                    val sessionsThisMonth =
                        confirmed.count { it.sessionDate.startsWith(currentMonth) }

                    val lastSessionDate = confirmed.maxByOrNull { it.sessionDate }?.sessionDate

                    mutableStateFlow.value = state.copy(
                        patient = patient,
                        sessions = sessions,
                        sessionsDisplay = sessions.map { s ->
                            SessionDisplay(
                                session = s,
                                dateDisplay = dateFormatter.format(s.sessionDate) ?: s.sessionDate,
                                feeDisplay = currencyFormatter.format(s.fee),
                                amountPaidDisplay = currencyFormatter.format(s.amountPaid),
                            )
                        },
                        totalOwed = totalOwed,
                        totalOwedDisplay = currencyFormatter.format(totalOwed),
                        defaultFeeDisplay = currencyFormatter.format(patient.defaultFee),
                        sessionsThisMonth = sessionsThisMonth,
                        lastSessionDate = lastSessionDate,
                        isLoading = false,
                        error = null,
                    )
                }
        }
    }
}
